// Company stores — Sales → Company stores (S2, CEO 2026-10-12; spec
// docs/company-stores-mvp.md). Every store with its status, products and
// people; New store opens the editor. Reads GET /company-stores (S1). Until
// Jake runs scripts/push-company-stores.ts the API answers 503 and this page
// says so calmly instead of erroring.
//
// Pattern: ClientsListPage (PageHeader + FilterTabs + design table).
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { FlashBanner } from "@/components/common/FlashBanner";
import { Button } from "@/components/ui/Button";
import { PageHeader } from "@/components/ui/PageHeader";
import { FilterTabs, TABLE_HEAD, TABLE_WRAP, TD, TH, TROW } from "@/components/ui/RecordParts";
import { StatusPill } from "@/components/ui/StatusPill";
import { api, apiErrorMessage } from "@/lib/api";
import {
  NOT_SET_UP_COPY,
  STATUS_LABEL,
  STATUS_TONE,
  isNotSetUp,
  shortDate,
  type StoreListRow,
  type StoreStatus,
} from "@/lib/companyStores";

/** Who may build and change a store — the API's STORE_EDITOR_ROLES. */
export const STORE_EDITOR_ROLES = ["admin", "client_rep", "order_rep"] as const;

const FILTERS: ReadonlyArray<{ value: StoreStatus | undefined; label: string }> = [
  { value: undefined, label: "All" },
  { value: "draft", label: STATUS_LABEL.draft },
  { value: "awaiting_signoff", label: STATUS_LABEL.awaiting_signoff },
  { value: "live", label: STATUS_LABEL.live },
  { value: "paused", label: STATUS_LABEL.paused },
];

export function CompanyStoresListPage() {
  const { user } = useAuth();
  const canEdit = STORE_EDITOR_ROLES.some((r) => user?.roles.includes(r));
  const [status, setStatus] = useState<StoreStatus | undefined>(undefined);

  const q = useQuery({
    queryKey: ["company-stores", "list"],
    queryFn: () => api<{ stores: StoreListRow[] }>("/company-stores"),
    retry: (n, e) => !isNotSetUp(e) && n < 2,
  });
  const all = q.data?.stores ?? [];
  const rows = useMemo(() => (status ? all.filter((s) => s.status === status) : all), [all, status]);
  const countFor = (s: StoreStatus | undefined) => (s ? all.filter((r) => r.status === s).length : all.length);

  return (
    <div className="mx-auto w-full">
      <FlashBanner />
      <PageHeader
        eyebrow={<>Sales / <span>Company stores</span></>}
        title="Company stores"
        intro="Online stores where a client's people order merch with their logo, paid the way the company chooses."
        action={
          canEdit && !isNotSetUp(q.error) ? (
            <Button asChild variant="primary">
              <Link to="/sales/company-stores/new">
                <Plus className="h-4 w-4" aria-hidden /> New store
              </Link>
            </Button>
          ) : undefined
        }
      />

      {isNotSetUp(q.error) ? (
        <p role="status" className="rounded-md border border-rule bg-canvas px-4 py-3 text-sm text-ink">
          {NOT_SET_UP_COPY}
        </p>
      ) : (
        <>
          <div className="mb-4 border-b border-rule">
            <FilterTabs
              ariaLabel="Filter by status"
              options={FILTERS.map((f) => ({
                value: f.value,
                label: q.data ? `${f.label} (${countFor(f.value)})` : f.label,
              }))}
              value={status}
              onChange={setStatus}
            />
          </div>

          <div className={TABLE_WRAP} role="region" aria-label="Company stores" tabIndex={0}>
            {q.isLoading ? (
              <div className="space-y-3 p-5" aria-label="Loading stores">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-4 w-full animate-pulse rounded bg-canvas" />
                ))}
              </div>
            ) : q.isError ? (
              <div className="p-5 text-sm text-risk" role="alert">
                {apiErrorMessage(q.error, "We couldn't load the stores.")} Refresh to try again.
              </div>
            ) : rows.length === 0 ? (
              <div className="px-5 py-14 text-center">
                <p className="font-medium text-ink">{all.length ? "No stores with this status." : "No company stores yet."}</p>
                {all.length === 0 ? (
                  <p className="mx-auto mt-1 max-w-lg text-sm text-muted">
                    Build a store for a client with their logo and products, send it to them to sign off,
                    and once they approve it their people can order from it.
                  </p>
                ) : null}
              </div>
            ) : (
              <table className="w-full text-left">
                <thead className={TABLE_HEAD}>
                  <tr>
                    <th scope="col" className={TH}>Store</th>
                    <th scope="col" className={TH}>Client</th>
                    <th scope="col" className={TH}>Status</th>
                    <th scope="col" className={`${TH} text-right`}>Products</th>
                    <th scope="col" className={`${TH} text-right`}>People</th>
                    <th scope="col" className={`${TH} hidden text-right sm:table-cell`}>Orders</th>
                    <th scope="col" className={`${TH} hidden sm:table-cell`}>Created</th>
                  </tr>
                </thead>
                <tbody className="text-[13px] tabular-nums">
                  {rows.map((s) => (
                    <tr key={s.id} className={TROW}>
                      <td className={TD}>
                        <Link to={`/sales/company-stores/${s.id}`} className="font-medium text-ink hover:underline">
                          {s.name}
                        </Link>
                        {s.repName ? <span className="mt-0.5 block text-[11px] text-muted">Rep: {s.repName}</span> : null}
                      </td>
                      <td className={TD}>
                        <Link to={`/clients/${s.clientId}`} className="text-ink hover:underline">
                          {s.clientName ?? "—"}
                        </Link>
                      </td>
                      <td className={TD}>
                        <StatusPill tone={STATUS_TONE[s.status]} className="normal-case">{STATUS_LABEL[s.status]}</StatusPill>
                        {s.status === "draft" && s.signoffNote ? (
                          <StatusPill tone="warn" className="ml-1.5 normal-case">Changes requested</StatusPill>
                        ) : null}
                      </td>
                      <td className={`${TD} text-right`}>{s.productCount}</td>
                      <td className={`${TD} text-right`}>{s.memberCount}</td>
                      <td className={`${TD} hidden text-right sm:table-cell`}>
                        {s.orderCount}
                        {s.pendingApprovalCount ? (
                          <span className="ml-1 text-warn">({s.pendingApprovalCount} to approve)</span>
                        ) : null}
                      </td>
                      <td className={`${TD} hidden text-muted sm:table-cell`}>{shortDate(s.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
